import React, { useState, useEffect } from 'react';
import '../styles/formStyles.css';
import { showAppAlert } from '../utils/alertService';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function maskCelular(value) {
  if (!value) return '';
  return value
    .replace(/\D/g, '')
    .replace(/^(\d{2})(\d)/g, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
}

function EditCompanyModal({ company, isOpen, onClose, onCompanyUpdated }) {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [policyData, setPolicyData] = useState({ signed: true, textPolicy: '', policy_id: null });
  const [isPolicyAccepted, setIsPolicyAccepted] = useState(false);
  const [isPolicyModalVisible, setIsPolicyModalVisible] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [planos, setPlanos] = useState([]);
  const token = localStorage.getItem('token');

  const isLojista = userProfile === 'lojista';

  useEffect(() => {
    if (isOpen) {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUserProfile(parsedUser.perfil || null);
        } catch {
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }
    }
    if (isOpen && company) {
      const companyDataWithStatusString = {
        ...company,
        status: company.status === 1 || company.status === 'ativo' ? 'ativo' : 'inativo',
      };
      setFormData(companyDataWithStatusString);
    }

    const fetchCompanyData = async (companyId) => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/companies/${companyId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          const statusString = data.status === 1 || data.status === 'ativo' ? 'ativo' : 'inativo';
          setFormData(prevData => ({
            ...prevData,
            ...data,
            primeiroAcesso: company.primeiroAcesso,
            status: statusString,
          }));
        } else {
          showAppAlert('error', 'Erro ao carregar dados da empresa.');
        }
      } catch (error) {
        showAppAlert('error', 'Erro de conexão ao carregar dados.');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && company) {
      // Busca dados adicionais apenas se for o primeiro acesso.
      if (Number(company.primeiroAcesso) === 1 && company.id) {
        fetchCompanyData(company.id);
      }
    } else if (!isOpen) {
      setFormData({});
      setConfirmPassword('');
    }
  }, [isOpen, company, token]);

  // Busca planos quando o modal abre
  useEffect(() => {
    if (isOpen) {
      fetch(`${API_URL}/plans/active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
        .then((res) => res.json())
        .then((data) => setPlanos(Array.isArray(data) ? data : []))
        .catch(() => setPlanos([]));
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (isOpen) {
      const userData = localStorage.getItem('user');
      let userProfile = null;
      let userId = null;
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          userId = parsedUser.user_id || parsedUser.id || parsedUser.id_usuario;
          userProfile = parsedUser.perfil;
        } catch {
          userId = null;
        }
      }
      if (userId && token && userProfile !== 'adm') {
        fetch(`${API_URL}/policy/check/${userId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
          .then(res => res.json())
          .then(data => {
            if (data) {
              setPolicyData({
                signed: data.signed,
                textPolicy: data.textPolicy,
                policy_id: data.id,
              });
            }
          })
          .catch(err => console.error("Failed to fetch policy", err));
      }
    }
  }, [isOpen, token]);

  if (!isOpen) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>Carregando...</h3>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const companyId = formData.id || localStorage.getItem('company_id');

    if (!companyId) {
      showAppAlert('error', 'ID da empresa não encontrado. Não é possível atualizar.');
      return;
    }

    // Perfil lojista: apenas alteração de senha
    if (isLojista) {
      if (!formData.password || formData.password.trim() === '') {
        showAppAlert('warning', 'Informe a nova senha.');
        return;
      }
      if (formData.password !== confirmPassword) {
        showAppAlert('warning', 'Senha e confirmação de senha não conferem.');
        return;
      }
      try {
        const response = await fetch(`${API_URL}/companies/${companyId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ password: formData.password, primeiroAcesso: 0 }),
        });
        if (response.ok) {
          showAppAlert('success', 'Senha alterada com sucesso!');
          setConfirmPassword('');
          setTimeout(() => {
            onCompanyUpdated();
          }, 1500);
        } else {
          const errorData = await response.json();
          const errorMessage = errorData.message || 'Erro ao alterar senha.';
          showAppAlert('error', errorMessage);
        }
      } catch (err) {
        showAppAlert('error', 'Erro de conexão ao alterar senha.');
      }
      return;
    }

    // Validação de senha obrigatória no primeiro acesso
    if (Number(formData.primeiroAcesso) === 1 && (!formData.password || formData.password.trim() === '')) {
      showAppAlert('warning', 'A troca da senha é obrigatória no primeiro acesso.');
      return;
    }

    if (policyData && !policyData.signed && !isPolicyAccepted) {
      showAppAlert('warning', 'Você deve aceitar os Termos e Políticas para continuar.');
      return;
    }

    let payload = {
      nomeFantasia: formData.nomeFantasia,
      telefone: (formData.telefone || '').replace(/\D/g, ''),
      nomeResponsavel: formData.nomeResponsavel,
      urlLogo: formData.urlLogo || null,
      valor_ponto: parseFloat(formData.valor_ponto),
      dias_ausencia: parseInt(formData.dias_ausencia, 10),
      planoId: Number(formData.planoId) || null,
      placeid: formData.placeid || null,
      telegramGroupId: formData.telegramGroupId || null,
      status: formData.status === 'ativo' ? 1 : 0,
    };

    if (policyData && !policyData.signed && isPolicyAccepted) {
      payload = {
        ...payload,
        signed: true,
        policy_id: policyData.policy_id,
      };
    }

    if (formData.password && formData.password.trim() !== '') {
      payload.password = formData.password;
    }

    if (Number(formData.primeiroAcesso) === 1) {
      payload.primeiroAcesso = 0;
      payload.status = 1;
    }

    const url = `${API_URL}/companies/${companyId}`;

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showAppAlert('success', 'Empresa atualizada com sucesso!');
        setTimeout(() => {
          onCompanyUpdated();
        }, 1500);
      } else {
        const errorData = await response.json();
        let errorMessage = errorData.message || 'Erro ao atualizar empresa.';
        if (errorData.errors) {
            const errorMessages = Object.values(errorData.errors).flat();
            errorMessage = errorMessages.join(' ');
        }
        showAppAlert('error', errorMessage);
      }
    } catch (err) {
      showAppAlert('error', 'Erro de conexão ao atualizar empresa.');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>{isLojista ? 'Alterar senha' : 'Editar Empresa'}</h3>
        <form onSubmit={handleSave}>
          {/* Perfil lojista: apenas senha e confirmação de senha */}
          {isLojista ? (
            <>
              <label className="label-style">
                Nova senha:
                <span style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  <input
                    type={isPasswordVisible ? 'text' : 'password'}
                    name="password"
                    value={formData.password || ''}
                    onChange={handleChange}
                    className="input-style"
                    required
                    autoComplete="new-password"
                  />
                  <span
                    onMouseDown={() => setIsPasswordVisible(true)}
                    onMouseUp={() => setIsPasswordVisible(false)}
                    onMouseLeave={() => setIsPasswordVisible(false)}
                    onTouchStart={() => setIsPasswordVisible(true)}
                    onTouchEnd={() => setIsPasswordVisible(false)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#8A2BE2"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </span>
                </span>
              </label>
              <label className="label-style">
                Confirmação de senha:
                <span style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  <input
                    type={isConfirmPasswordVisible ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-style"
                    required
                    autoComplete="new-password"
                    placeholder="Repita a nova senha"
                  />
                  <span
                    onMouseDown={() => setIsConfirmPasswordVisible(true)}
                    onMouseUp={() => setIsConfirmPasswordVisible(false)}
                    onMouseLeave={() => setIsConfirmPasswordVisible(false)}
                    onTouchStart={() => setIsConfirmPasswordVisible(true)}
                    onTouchEnd={() => setIsConfirmPasswordVisible(false)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#8A2BE2"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </span>
                </span>
              </label>
            </>
          ) : (
            <>
          <label className="label-style">
            Nome Fantasia:
            <input type="text" name="nomeFantasia" value={formData.nomeFantasia || ''} onChange={handleChange} className="input-style" required />
          </label>

          <label className="label-style">
            Responsável:
            <input type="text" name="nomeResponsavel" value={formData.nomeResponsavel || ''} onChange={handleChange} className="input-style" required />
          </label>

          <label className="label-style">
            Celular:
            <input type="text" name="telefone" value={maskCelular(formData.telefone || '')} onChange={e => setFormData({ ...formData, telefone: maskCelular(e.target.value) })} className="input-style" required />
          </label>

          {/* Campo de Senha, obrigatório no primeiro acesso */}
          {Number(formData.primeiroAcesso) === 1 && (
            <label className="label-style">
              Senha (obrigatória no 1º acesso):
              <span style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  name="password"
                  value={formData.password || ''}
                  onChange={handleChange}
                  className="input-style"
                  required={Number(formData.primeiroAcesso) === 1}
                  autoComplete="new-password"
                />
                <span
                  onMouseDown={() => setIsPasswordVisible(true)}
                  onMouseUp={() => setIsPasswordVisible(false)}
                  onMouseLeave={() => setIsPasswordVisible(false)}
                  onTouchStart={() => setIsPasswordVisible(true)}
                  onTouchEnd={() => setIsPasswordVisible(false)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8A2BE2"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </span>
              </span>
            </label>
          )}

          <label className="label-style">
            Logotipo (URL):
            <input type="text" name="urlLogo" value={formData.urlLogo || ''} onChange={handleChange} className="input-style" />
          </label>

          <label className="label-style">
            Valor do Ponto:
            <input type="number" name="valor_ponto" value={formData.valor_ponto || ''} onChange={handleChange} className="input-style" step="1.00" required />
          </label>

          <label className="label-style">
            Dias de Ausência:
            <input type="number" name="dias_ausencia" value={formData.dias_ausencia || ''} onChange={handleChange} className="input-style" required />
          </label>

          <label className="label-style">
            Plano:
            <select
              name="planoId"
              value={formData.planoId || ''}
              onChange={handleChange}
              className="input-style"
              required
            >
              <option value="">Selecione um plano</option>
              {planos.map((plano) => (
                <option key={plano.id} value={plano.id}>
                  {plano.nome} - {plano.valor}
                </option>
              ))}
            </select>
          </label>
          
          <label className="label-style">
            Status da Empresa:
            <select name="status" value={company.primeiroAcesso ? 'ativo' : (formData.status || '')} onChange={handleChange} className="input-style" required disabled={company.primeiroAcesso}>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </label>

          <label className="label-style">
            Place ID:
            <input type="text" name="placeid" value={formData.placeid || ''} onChange={handleChange} className="input-style" />
          </label>

          {(formData.qtdCompanies > 1) && (
          <label className="label-style">
            ID-Grupo Telegram:
            <input type="text" name="telegramGroupId" value={formData.telegramGroupId || ''} onChange={handleChange} className="input-style" placeholder="Ex: -5259244315" />
          </label>
          )}

          {policyData && !policyData.signed && (
            <div className="label-style" style={{ display: 'flex', alignItems: 'center', marginTop: '15px' }}>
              <input
                type="checkbox"
                id="acceptPolicyCompany"
                style={{ width: 'auto', margin: '0 8px 0 0' }}
                checked={isPolicyAccepted}
                onChange={(e) => setIsPolicyAccepted(e.target.checked)}
                required
              />
              <label htmlFor="acceptPolicyCompany" style={{ marginBottom: 0, fontWeight: 'normal' }}>
                Eu li e aceito os{' '}
                <button
                  type="button"
                  onClick={() => setIsPolicyModalVisible(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#007bff',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Termos e Políticas
                </button>
              </label>
            </div>
          )}

            </>
          )}

          <div className="button-container">
            <button type="submit" className="button-style">Salvar</button>
            <button type="button" className="button-style cancel" onClick={onClose} disabled={isLojista ? false : Number(formData.primeiroAcesso) === 1}>Cancelar</button>
          </div>
        </form>

        {isPolicyModalVisible && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Termos e Políticas</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }} dangerouslySetInnerHTML={{ __html: policyData.textPolicy }}></div>
              <button onClick={() => setIsPolicyModalVisible(false)} className="button-style">Fechar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EditCompanyModal;